import { useMemo } from "react";

import {
  DYNAMIC_EVENTS_REQUEST,
  DynamicEventsResponse,
  FunnelEvents,
  useInsightsEventDefs,
} from "@/api/insights";
import { useApiQuery } from "@/api";
import { useAccountEntitlements } from "@/entitlements/queries/useAccountEntitlements";
import useFeatureFlags from "@/hooks/useFeatureFlag";
import { ENDPOINTS } from "../constants";

export type UseConversionModalDataResult = {
  eventDefs: FunnelEvents[];
  eventDefsLoading: boolean;
  dynamicEventsData: DynamicEventsResponse | undefined;
  isLoadingDynamicEvents: boolean;
  hasConfiguredConversionEvents: boolean;
  showData: boolean;
  showRecommendationCards: boolean;
};

export function useConversionModalData(): UseConversionModalDataResult {
  const flag = useFeatureFlags();
  const { data: eventDefs = [], isLoading: eventDefsLoading } =
    useInsightsEventDefs();
  const { data: accountEntitlements } = useAccountEntitlements();
  const { data: envConfigResponse } = useApiQuery(["get_env_config", {}], {
    staleTime: Infinity,
  });
  const { data: dynamicEventsData, isLoading: isLoadingDynamicEvents } =
    useApiQuery([ENDPOINTS.GET_DYNAMIC_EVENTS, DYNAMIC_EVENTS_REQUEST]);

  const isDemoEnv = envConfigResponse?.envConfig?.isDemo ?? false;
  const hasRecommendationCardsEntitlement = Boolean(
    accountEntitlements?.account_access?.some(
      (access) => access.entitlement_key === "Recommendation Cards",
    ),
  );

  const hasConfiguredConversionEvents = useMemo(() => {
    if (!dynamicEventsData) return false;

    return (
      (Array.isArray(dynamicEventsData.midFunnel) &&
        dynamicEventsData.midFunnel.length > 0) ||
      (Array.isArray(dynamicEventsData.bottomFunnel) &&
        dynamicEventsData.bottomFunnel.length > 0)
    );
  }, [dynamicEventsData]);

  return {
    eventDefs,
    eventDefsLoading,
    dynamicEventsData,
    isLoadingDynamicEvents,
    hasConfiguredConversionEvents,
    showData: hasConfiguredConversionEvents || isDemoEnv,
    showRecommendationCards:
      (hasRecommendationCardsEntitlement && flag("contractAllowsAIUsage")) ||
      isDemoEnv,
  };
}
